import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Component, computed, inject, input, OnInit, output, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { PessoaDataService } from '@nomoh-monorepo/data-access-shared-services';
import { EnvironmentService } from '@nomoh-monorepo/shared-data-access-environment';
import { IPlano } from '@nomoh-monorepo/shared-types';
import { ConfirmationService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { DialogModule } from 'primeng/dialog';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { SelectButtonModule } from 'primeng/selectbutton';
import { firstValueFrom } from 'rxjs';
import { BottomSheetComponent } from '../bottom-sheet/bottom-sheet.component';
import { DeviceService } from '../device/device.service';

@Component({
  selector: 'lib-renovar-plano-modal',
  standalone: true,
  imports: [
    CommonModule,
    ButtonModule,
    ConfirmDialogModule,
    DialogModule,
    ProgressSpinnerModule,
    SelectButtonModule,
    FormsModule,
    BottomSheetComponent,
  ],
  providers: [ConfirmationService],
  templateUrl: './renovar-plano-modal.component.html',
  styleUrls: ['./renovar-plano-modal.component.scss'],
})
export class RenovarPlanoModalComponent implements OnInit {
  sistemaId = input.required<string>();
  pessoaId = input.required<string>();
  fechar = output<void>();

  readonly deviceService = inject(DeviceService);
  private pessoaDataService = inject(PessoaDataService);
  private environment = inject(EnvironmentService);
  private http = inject(HttpClient);
  private confirmationService = inject(ConfirmationService);

  private _planos = signal<IPlano[]>([]);
  private _loading = signal(false);
  private _processando = signal<string | null>(null);

  $planos = this._planos.asReadonly();
  $loading = this._loading.asReadonly();
  $processando = this._processando.asReadonly();
  visible = true;

  onVisibleChange(aberto: boolean): void {
    this.visible = aberto;
    if (!aberto) {
      this.fechar.emit();
    }
  }

  billingInterval: 'month' | 'year' = 'month';

  intervalOptions = [
    { label: 'Mensal', value: 'month' },
    { label: 'Anual', value: 'year' },
  ];

  $temPrecoAnual = computed(() => this._planos().some((p) => p.priceYearly));
  $catalogoTemZap = computed(() =>
    this._planos().some((p) => !!p.hasZapIntegration)
  );

  async ngOnInit() {
    this._loading.set(true);
    try {
      const todos = await this.pessoaDataService.buscarTodosPlanos();
      this._planos.set(
        todos.filter(
          (p) => p.sistemaId === this.sistemaId() && !p.isDefault && p.status
        )
      );
    } finally {
      this._loading.set(false);
    }
  }

  precoAtual(plano: IPlano): number | null {
    return this.billingInterval === 'year' ? plano.priceYearly : plano.priceMonthly;
  }

  avisoZapNoCheckout(plano: IPlano): boolean {
    return this.$catalogoTemZap() && !plano.hasZapIntegration;
  }

  async escolherPlano(plano: IPlano) {
    if (this.avisoZapNoCheckout(plano)) {
      this.confirmationService.confirm({
        header: 'WhatsApp será desconectado',
        message:
          'Este plano não inclui WhatsApp Zap. A sessão atual será encerrada após o pagamento. Você continua logado no Bardo.',
        icon: 'pi pi-exclamation-triangle',
        acceptLabel: 'Continuar para pagamento',
        rejectLabel: 'Voltar',
        accept: () => {
          void this.irParaCheckout(plano);
        },
      });
      return;
    }
    await this.irParaCheckout(plano);
  }

  private async irParaCheckout(plano: IPlano) {
    this._processando.set(plano.id);
    try {
      const url = `${this.environment.getApiUrl('nomoh')}/pagamento/checkout`;
      const { url: checkoutUrl } = await firstValueFrom(
        this.http.post<{ sessionId: string; url: string }>(url, {
          planoId: plano.id,
          pessoaId: this.pessoaId(),
          billingInterval: this.billingInterval,
          returnOrigin: window.location.origin,
        })
      );
      window.location.href = checkoutUrl;
    } finally {
      this._processando.set(null);
    }
  }

  formatarPreco(valor: number | null): string {
    if (!valor) return 'Não disponível';
    return valor.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  }
}
